<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { downloadFile } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import PhotoPane from './PhotoPane.vue';

const props = defineProps({
  photos: {
    type: Array,
    required: true,
  },
  initialSlot: {
    type: String,
    default: '',
  },
  canRegenerate: {
    type: Boolean,
    default: false,
  },
  regenerateTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'setPrimary', 'regenerate']);

const { t } = useI18n();
const isRTL = useMapGetter('accounts/isRTL');

const MODES = ['compare', 'split', 'single'];
const MAX_SCALE = 6;

const mode = ref('compare');
const source = ref('generated');
const wipe = ref(50);
const scale = ref(1);
const offset = ref({ x: 0, y: 0 });
const isPanning = ref(false);
const isDownloading = ref(false);

const compareRef = ref(null);
let panOrigin = { x: 0, y: 0 };

const index = ref(
  Math.max(
    0,
    props.photos.findIndex(photo => photo.slot === props.initialSlot)
  )
);

const photo = computed(() => props.photos[index.value] || {});
const hasGenerated = computed(() => !!photo.value.generated_preview_url);
const isGenerating = computed(
  () => photo.value.generation_status === 'pending'
);
const generationFailed = computed(
  () => photo.value.generation_status === 'failed'
);

// Compare and split need both sides; a slot without an AI image falls back to
// the plain single view until its generation lands.
const activeMode = computed(() => (hasGenerated.value ? mode.value : 'single'));
const isCompare = computed(() => activeMode.value === 'compare');
const isSplit = computed(() => activeMode.value === 'split');

const showsGenerated = computed(
  () => hasGenerated.value && source.value === 'generated'
);
const singleUrl = computed(() =>
  showsGenerated.value
    ? photo.value.generated_preview_url
    : photo.value.preview_url
);
const singleCardUrl = computed(() =>
  showsGenerated.value ? photo.value.generated_card_url : photo.value.card_url
);

const slotLabel = computed(() =>
  photo.value.slot ? t(`WGPT_CAR_LISTINGS.PHOTO_SLOTS.${photo.value.slot}`) : ''
);

const resetView = () => {
  scale.value = 1;
  offset.value = { x: 0, y: 0 };
};

const zoomBy = delta => {
  const next = Math.min(MAX_SCALE, Math.max(1, scale.value + delta));
  scale.value = next;
  if (next === 1) offset.value = { x: 0, y: 0 };
};

const transformStyle = computed(() => ({
  transform: `translate3d(${offset.value.x}px, ${offset.value.y}px, 0) scale(${scale.value})`,
}));

const onWheel = event => zoomBy(-event.deltaY * 0.003 * scale.value);

const onDoubleClick = () => (scale.value > 1 ? resetView() : zoomBy(1.5));

const onPanStart = event => {
  if (scale.value === 1) return;
  isPanning.value = true;
  panOrigin = {
    x: event.clientX - offset.value.x,
    y: event.clientY - offset.value.y,
  };
  event.currentTarget.setPointerCapture(event.pointerId);
};

const onPanMove = event => {
  if (!isPanning.value) return;
  offset.value = {
    x: event.clientX - panOrigin.x,
    y: event.clientY - panOrigin.y,
  };
};

const onPanEnd = () => {
  isPanning.value = false;
};

// The wipe is measured in screen space so the divider stays put while the
// images pan and zoom underneath it.
const isWiping = ref(false);
const moveWipe = event => {
  const rect = compareRef.value?.getBoundingClientRect();
  if (!rect) return;
  const ratio = (event.clientX - rect.left) / rect.width;
  wipe.value = Math.min(100, Math.max(0, ratio * 100));
};

const onWipeStart = event => {
  isWiping.value = true;
  event.target.setPointerCapture(event.pointerId);
};

const onWipeMove = event => {
  if (isWiping.value) moveWipe(event);
};

const onWipeEnd = () => {
  isWiping.value = false;
};

// clip-path insets are physical, so the AI layer is revealed from whichever
// edge is trailing. That keeps the original on the leading side, matching the
// split view and the pane labels in both directions.
const generatedClip = computed(() =>
  isRTL.value
    ? `inset(0 ${100 - wipe.value}% 0 0)`
    : `inset(0 0 0 ${wipe.value}%)`
);

const go = step => {
  const next = index.value + step;
  if (next < 0 || next >= props.photos.length) return;
  index.value = next;
};

const goTo = next => {
  index.value = next;
};

watch(
  index,
  () => {
    resetView();
    wipe.value = 50;
    // Warm the neighbours so arrowing through the slots feels instant.
    [index.value - 1, index.value + 1].forEach(neighbour => {
      const item = props.photos[neighbour];
      if (!item) return;
      [item.generated_preview_url, item.preview_url]
        .filter(Boolean)
        .forEach(url => {
          new Image().src = url;
        });
    });
  },
  { immediate: true }
);

const downloadUrl = computed(() =>
  isCompare.value || isSplit.value || showsGenerated.value
    ? photo.value.generated_file_url || photo.value.file_url
    : photo.value.file_url
);

const onDownload = async () => {
  const url = downloadUrl.value;
  if (!url) return;
  try {
    isDownloading.value = true;
    const extension = url.split('?')[0].split('.').pop();
    await downloadFile({ url, type: 'image', extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const onClose = () => emit('close');

useKeyboardEvents({
  Escape: { action: onClose },
  ArrowLeft: { action: () => go(-1) },
  ArrowRight: { action: () => go(1) },
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      class="fixed inset-0 z-[9990] flex flex-col select-none bg-n-background"
    >
      <header
        class="flex items-center gap-3 px-4 py-2 border-b shrink-0 h-14 border-n-weak"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ slotLabel }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ `${index + 1} / ${photos.length}` }}
          </span>
        </div>

        <div
          class="flex p-0.5 mx-auto rounded-lg bg-n-alpha-1"
          :class="{ 'opacity-50': !hasGenerated }"
        >
          <button
            v-for="item in MODES"
            :key="item"
            type="button"
            class="px-3 py-1 text-xs font-medium transition-colors rounded-md disabled:cursor-not-allowed"
            :class="
              activeMode === item
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            :disabled="!hasGenerated && item !== 'single'"
            @click="mode = item"
          >
            {{ t(`WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.MODES.${item}`) }}
          </button>
        </div>

        <div class="flex items-center gap-1">
          <div
            v-if="activeMode === 'single' && hasGenerated"
            class="flex p-0.5 rounded-lg bg-n-alpha-1"
          >
            <button
              v-for="item in ['generated', 'original']"
              :key="item"
              type="button"
              class="px-2 py-1 text-xs font-medium transition-colors rounded-md"
              :class="
                source === item
                  ? 'bg-n-solid-1 text-n-slate-12'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="source = item"
            >
              {{
                t(
                  `WGPT_CAR_LISTINGS.EDITOR.PHOTOS.${
                    item === 'generated' ? 'GENERATED' : 'ORIGINAL'
                  }`
                )
              }}
            </button>
          </div>
          <Button
            icon="i-lucide-zoom-out"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="scale === 1"
            :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.ZOOM_OUT')"
            @click="zoomBy(-0.5)"
          />
          <Button
            icon="i-lucide-zoom-in"
            variant="ghost"
            color="slate"
            size="sm"
            :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.ZOOM_IN')"
            @click="zoomBy(0.5)"
          />
          <Button
            v-if="!photo.primary"
            icon="i-lucide-star"
            variant="ghost"
            color="slate"
            size="sm"
            :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.SET_PRIMARY')"
            @click="emit('setPrimary', photo.slot)"
          />
          <Button
            icon="i-lucide-sparkles"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="!canRegenerate || isGenerating"
            :title="regenerateTitle"
            @click="emit('regenerate', photo.slot)"
          />
          <Button
            icon="i-lucide-download"
            variant="ghost"
            color="slate"
            size="sm"
            :is-loading="isDownloading"
            :disabled="isDownloading"
            :title="t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.DOWNLOAD')"
            @click="onDownload"
          />
          <Button
            icon="i-lucide-x"
            variant="ghost"
            color="slate"
            size="sm"
            @click="onClose"
          />
        </div>
      </header>

      <main class="relative flex items-stretch flex-1 min-h-0">
        <Button
          v-if="photos.length > 1"
          icon="ltr:i-lucide-chevron-left rtl:i-lucide-chevron-right"
          class="absolute z-10 -translate-y-1/2 top-1/2 start-4"
          color="slate"
          variant="faded"
          :disabled="index === 0"
          @click="go(-1)"
        />
        <Button
          v-if="photos.length > 1"
          icon="ltr:i-lucide-chevron-right rtl:i-lucide-chevron-left"
          class="absolute z-10 -translate-y-1/2 top-1/2 end-4"
          color="slate"
          variant="faded"
          :disabled="index === photos.length - 1"
          @click="go(1)"
        />

        <div
          class="flex flex-1 min-w-0 gap-1 p-4 mx-16"
          :class="{ 'cursor-grab': scale > 1, 'cursor-grabbing': isPanning }"
          @wheel.prevent="onWheel"
          @dblclick="onDoubleClick"
          @pointerdown="onPanStart"
          @pointermove="onPanMove"
          @pointerup="onPanEnd"
          @pointercancel="onPanEnd"
        >
          <!-- Compare: the AI image is wiped over the original by the handle. -->
          <div
            v-if="isCompare"
            ref="compareRef"
            class="relative flex-1 min-w-0 overflow-hidden rounded-lg bg-n-alpha-1"
          >
            <PhotoPane
              :url="photo.preview_url"
              :low-res-url="photo.card_url"
              :transform="transformStyle"
              :label="slotLabel"
            />
            <div class="absolute inset-0" :style="{ clipPath: generatedClip }">
              <PhotoPane
                :url="photo.generated_preview_url"
                :low-res-url="photo.generated_card_url"
                :transform="transformStyle"
                :label="slotLabel"
              />
            </div>

            <span
              class="absolute px-2 py-1 text-xs font-medium text-white rounded-md start-3 top-3 bg-[#000000]/60"
            >
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ORIGINAL') }}
            </span>
            <span
              class="absolute inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md end-3 top-3 bg-n-teal-9"
            >
              <span class="block i-lucide-sparkles" />
              {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATED') }}
            </span>

            <div
              class="absolute top-0 bottom-0 z-10 w-0.5 bg-white/90 cursor-ew-resize touch-none"
              :style="{ left: `${wipe}%` }"
              @pointerdown.stop="onWipeStart"
              @pointermove.stop="onWipeMove"
              @pointerup.stop="onWipeEnd"
              @pointercancel.stop="onWipeEnd"
              @dblclick.stop
            >
              <span
                class="absolute flex items-center justify-center text-white -translate-x-1/2 -translate-y-1/2 rounded-full shadow-lg size-9 top-1/2 left-1/2 bg-n-teal-9"
              >
                <span class="block i-lucide-chevrons-left-right" />
              </span>
            </div>
          </div>

          <!-- Split: both images side by side, panning and zooming together. -->
          <template v-else-if="isSplit">
            <div
              class="relative flex-1 min-w-0 overflow-hidden rounded-lg bg-n-alpha-1"
            >
              <PhotoPane
                :url="photo.preview_url"
                :low-res-url="photo.card_url"
                :transform="transformStyle"
                :label="slotLabel"
              />
              <span
                class="absolute px-2 py-1 text-xs font-medium text-white rounded-md start-3 top-3 bg-[#000000]/60"
              >
                {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.ORIGINAL') }}
              </span>
            </div>
            <div
              class="relative flex-1 min-w-0 overflow-hidden rounded-lg bg-n-alpha-1"
            >
              <PhotoPane
                :url="photo.generated_preview_url"
                :low-res-url="photo.generated_card_url"
                :transform="transformStyle"
                :label="slotLabel"
              />
              <span
                class="absolute inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-md start-3 top-3 bg-n-teal-9"
              >
                <span class="block i-lucide-sparkles" />
                {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATED') }}
              </span>
            </div>
          </template>

          <div
            v-else
            class="relative flex-1 min-w-0 overflow-hidden rounded-lg bg-n-alpha-1"
          >
            <PhotoPane
              :url="singleUrl"
              :low-res-url="singleCardUrl"
              :transform="transformStyle"
              :label="slotLabel"
            />
          </div>
        </div>
      </main>

      <footer
        class="flex flex-col gap-2 px-4 py-3 border-t shrink-0 border-n-weak"
      >
        <p class="text-xs text-center text-n-slate-10">
          <span v-if="isGenerating">
            {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.PENDING') }}
          </span>
          <span v-else-if="generationFailed">
            {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.GENERATION_FAILED') }}
          </span>
          <span v-else-if="!hasGenerated">
            {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.NO_GENERATED') }}
          </span>
          <span v-else>
            {{ t('WGPT_CAR_LISTINGS.EDITOR.PHOTOS.VIEWER.HINT') }}
          </span>
        </p>
        <div class="flex justify-center gap-2 overflow-x-auto">
          <button
            v-for="(item, itemIndex) in photos"
            :key="item.slot"
            type="button"
            class="relative overflow-hidden transition-all border-2 rounded-md shrink-0 h-14 w-20"
            :class="
              itemIndex === index
                ? 'border-n-brand'
                : 'border-transparent opacity-60 hover:opacity-100'
            "
            :title="t(`WGPT_CAR_LISTINGS.PHOTO_SLOTS.${item.slot}`)"
            @click="goTo(itemIndex)"
          >
            <img
              :src="item.generated_thumb_url || item.thumb_url"
              :alt="t(`WGPT_CAR_LISTINGS.PHOTO_SLOTS.${item.slot}`)"
              class="object-cover w-full h-full"
              loading="lazy"
              decoding="async"
            />
            <span
              v-if="item.generated_thumb_url"
              class="absolute p-0.5 text-white rounded bottom-0.5 end-0.5 bg-n-teal-9"
            >
              <span class="block text-[0.625rem] i-lucide-sparkles" />
            </span>
          </button>
        </div>
      </footer>
    </div>
  </TeleportWithDirection>
</template>
